import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  makeStyles,
  Link,
  Tooltip,
  styled,
  withStyles,
} from '@material-ui/core';
import { z } from 'zod';
import Form from '../../Common/Form';
import InputGroup from '../../Common/Form/InputGroup';
import KobeZoneFields from './KobeZoneFields';
import TransferTable from '../../Common/Form/TransferTable';
import { formatDate } from '../../../helpers/date';
import { formatToPercentage } from '../../../helpers/number';

const validationSchema = z
  .object({
    kobe_zones: z.array(
      z.object({
        zone: z.string(),
        floor: z
          .number()
          .positive('Floor must be greater than 0.')
          .nullable(),
        ceiling: z
          .number()
          .positive('Ceiling must be greater than 0.')
          .nullable(),
      })
    ),
    reason: z
      .string()
      .trim()
      .nonempty(),
  })
  .superRefine((val, ctx) => {
    val.kobe_zones.forEach((zone, index) => {
      if (zone.zone && zone.ceiling === null && zone.floor === null) {
        if (zone.ceiling === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`kobe_zones.${index}.ceiling`],
            message: 'Please add Floor or Ceiling values.',
            fatal: true,
          });
        }

        if (zone.floor === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`kobe_zones.${index}.floor`],
            message: 'Please add Floor or Ceiling values.',
            fatal: true,
          });
        }

        return z.NEVER;
      }

      if (zone.ceiling !== null && zone.floor > zone.ceiling) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`kobe_zones.${index}.floor`],
          message: 'Floor shouldn’t exceed ceiling.',
          fatal: true,
        });
        return z.NEVER;
      }

      if (zone.floor !== null && zone.ceiling === zone.floor) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`kobe_zones.${index}.ceiling`],
          message: 'Ceiling must be greater than floor',
          fatal: true,
        });
      }

      if (zone.zone === null && (zone.floor !== null || zone.ceiling !== null)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`kobe_zones.${index}.zone`],
          message: 'Please select a zone.',
          fatal: true,
        });
        return z.NEVER;
      }

      return z.NEVER;
    });

    return z.NEVER;
  });

const DEFAULT_FORM_VALUES = {
  kobe_zones: [],
  reason: '',
};

const TRANSFER_TABLE_COLUMNS = [
  { id: 'rank', header: 'Rank', align: 'center', width: 40 },
  { id: 'lobsang_event_id', header: 'Event ID', width: 75 },
  { id: 'performer', header: 'Event Name', width: 90 },
  { id: 'venue', header: 'Venue', width: 80 },
  { id: 'event_date', header: 'Date', width: 70 },
  { id: 'similarity', header: 'Similarity', align: 'center', width: 50 },
];

const useStyles = makeStyles(theme => ({
  chipDisabled: {
    background: '#e7cbec',
    color: theme.palette.primary.main,
    '&:hover': {
      background: '#e7cbec',
    },
    '&:focus': {
      background: '#e7cbec',
    },
  },
  required: {
    color: theme.palette.type === 'light' ? '#C41212' : theme.palette.error.dark,
  },
  label: {
    marginBottom: theme.spacing(2),
    fontSize: '14px',
  },
  helperText: {
    fontSize: '14px',
    lineHeight: '1.2',
    marginTop: 0,
    marginBottom: theme.spacing(1.5),
    marginLeft: theme.spacing(0.625),
  },
  groupError: {
    color: theme.palette.type === 'light' ? '#C41212' : theme.palette.error.dark,
    display: 'inline-block',
    marginTop: theme.spacing(2),
  },
}));

const StyledTooltip = withStyles({
  tooltip: {
    fontSize: '14px',
  },
})(Tooltip);

const TruncatedLink = styled(Link)(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
}));

const TruncatedText = styled('div')(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const propTypes = {
  automationData: PropTypes.shape({
    kobe_included_proxies: PropTypes.arrayOf(PropTypes.number),
    kobe_excluded_proxies: PropTypes.arrayOf(PropTypes.number),
    kobe_zones: PropTypes.arrayOf(
      PropTypes.shape({
        zone: PropTypes.string,
        floor: PropTypes.number,
        ceiling: PropTypes.number,
      })
    ),
    reason: PropTypes.string,
  }),
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSuccess: PropTypes.bool,
  kobeZones: PropTypes.arrayOf(PropTypes.string).isRequired,
  proxyEvents: PropTypes.arrayOf(
    PropTypes.shape({
      lobsang_event_id: PropTypes.number,
      event_date: PropTypes.string,
      performer: PropTypes.string,
      rank: PropTypes.number,
      similarity: PropTypes.number,
      venue: PropTypes.string,
    })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func,
};
const defaultProps = {
  automationData: null,
  isError: false,
  isLoading: false,
  isSuccess: false,
  onReset: undefined,
};

const areIdsEqual = (oldIds, newIds) => {
  if (oldIds.length !== newIds.length) {
    return false;
  }

  const sortedOldIds = oldIds.slice().sort();
  const sortedNewIds = newIds.slice().sort();

  return sortedOldIds.every((id, index) => id === sortedNewIds[index]);
};

const compareProxyEvents = (a, b) => a.rank - b.rank;

const formatProxyEvent = proxyEvent => ({
  ...proxyEvent,
  id: proxyEvent.lobsang_event_id, // Required for TransferTable.
  performer: (
    <StyledTooltip title={proxyEvent.performer} placement="top" arrow>
      <TruncatedLink
        href={`${process.env.KONAMI_URL}/event/dashboard/${proxyEvent.lobsang_event_id}`}
        rel="noopener noreferrer"
        target="_blank"
      >
        {proxyEvent.performer}
      </TruncatedLink>
    </StyledTooltip>
  ),
  venue: (
    <StyledTooltip title={proxyEvent.venue} placement="top" arrow>
      <TruncatedText>{proxyEvent.venue}</TruncatedText>
    </StyledTooltip>
  ),
  event_date: formatDate(proxyEvent.event_date),
  similarity: formatToPercentage(proxyEvent.similarity),
});

function KobeForm({ automationData, isError, isLoading, isSuccess, kobeZones, proxyEvents, onSubmit, onReset }) {
  const classes = useStyles();
  const hasKobeZones = kobeZones.length > 0;
  const [excludedProxyEventIds, setExcludedProxyEventIds] = useState(automationData?.kobe_excluded_proxies ?? []);

  // Memoize the values to prevent the form from resetting during re-renders.
  const initialValues = useMemo(() => {
    if (!automationData) {
      return DEFAULT_FORM_VALUES;
    }

    // eslint-disable-next-line camelcase
    const { kobe_zones } = automationData;

    return {
      kobe_zones,
      reason: '',
    };
  }, [automationData]);

  const includedProxyEvents = proxyEvents
    .filter(event => !excludedProxyEventIds.includes(event.lobsang_event_id))
    .sort(compareProxyEvents)
    .map(formatProxyEvent);
  const excludedProxyEvents = proxyEvents
    .filter(event => excludedProxyEventIds.includes(event.lobsang_event_id))
    .sort(compareProxyEvents)
    .map(formatProxyEvent);

  const handleProxyEventsChange = (_, excludedItems) => {
    setExcludedProxyEventIds(excludedItems.map(proxyEvent => proxyEvent.lobsang_event_id));
  };

  const handleSubmit = formValues => {
    onSubmit({
      ...formValues,
      kobe_included_proxies: includedProxyEvents.map(proxyEvent => proxyEvent.lobsang_event_id),
      kobe_excluded_proxies: excludedProxyEvents.map(proxyEvent => proxyEvent.lobsang_event_id),
    });
  };

  // Update excluded proxy events when the automation data changes.
  useEffect(() => {
    if (!areIdsEqual(automationData?.kobe_excluded_proxies ?? [], excludedProxyEventIds)) {
      setExcludedProxyEventIds(automationData?.kobe_excluded_proxies ?? []);
    }
  }, [automationData?.kobe_excluded_proxies]);

  return (
    <Form
      initialValues={initialValues}
      isError={isError}
      isLoading={isLoading}
      isSuccess={isSuccess}
      validationSchema={validationSchema}
      values={initialValues}
      onSubmit={formValues => handleSubmit(formValues)}
      onReset={onReset}
    >
      {({ control, formState: { errors }, trigger, watch }) => (
        <>
          <InputGroup
            label={
              <>
                Add KOBE Foor/Ceiling and Zone to apply. (Optional)
                {!hasKobeZones && (
                  <span className={classes.groupError}>
                    Groups must be created to apply Kobe floor and ceiling successfully.
                  </span>
                )}
              </>
            }
          >
            <KobeZoneFields name="kobe_zones" formMethods={{ control, errors, trigger, watch }} kobeZones={kobeZones} />
          </InputGroup>

          <InputGroup label="Remove Proxy Events. (Optional)">
            <TransferTable
              disabled={isLoading}
              columns={TRANSFER_TABLE_COLUMNS}
              actionColumnWidth={40}
              includedItems={includedProxyEvents}
              excludedItems={excludedProxyEvents}
              onChange={(included, excluded) => handleProxyEventsChange(included, excluded)}
            />
          </InputGroup>

          <FormControl fullWidth>
            <FormLabel classes={{ root: classes.label }}>
              Please provide a reason for this request.
              <span className={classes.required}>* </span>
              (Required)
            </FormLabel>
            <FormHelperText className={classes.helperText}>
              Previous reason:
              <br />
              {automationData?.reason}
            </FormHelperText>

            <Controller
              name="reason"
              control={control}
              render={({ field }) => <OutlinedInput placeholder="Type text here." rows={3} multiline {...field} />}
            />
          </FormControl>
        </>
      )}
    </Form>
  );
}

KobeForm.propTypes = propTypes;
KobeForm.defaultProps = defaultProps;

export default KobeForm;
